import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/CustomAuthContext';
import { authAPI } from '../../services/api';
import './Profile.css';

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // TOTP/MFA State
  const [totpSetupMode, setTotpSetupMode] = useState(false);
  const [totpUri, setTotpUri] = useState<string>('');
  const [totpSecret, setTotpSecret] = useState<string>('');
  const [totpVerifyCode, setTotpVerifyCode] = useState<string>('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState<boolean>(false);
  const [totpStatusLoading, setTotpStatusLoading] = useState(true);

  // Function to refresh user data
  const refreshUserData = async () => {
    try {
      const profileResponse = await authAPI.getProfile();
      updateUser(profileResponse.user);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  // Function to load TOTP status
  const loadTOTPStatus = async () => {
    try {
      setTotpStatusLoading(true);
      const status = await authAPI.getTOTPStatus();
      setTotpEnabled(status.totpEnabled);
    } catch (error) {
      console.error('Failed to load TOTP status:', error);
    } finally {
      setTotpStatusLoading(false);
    }
  };

  // Load TOTP status on mount
  useEffect(() => {
    if (user) {
      loadTOTPStatus();
    }
  }, [user]);

  // Handle OAuth callback messages
  useEffect(() => {
    const error = searchParams.get('error');
    const success = searchParams.get('success');

    if (error) {
      let errorMessage = 'An error occurred';
      switch (error) {
        case 'oauth_failed':
          errorMessage = 'Discord OAuth failed. Please try again.';
          break;
        case 'no_session':
          errorMessage = 'Session expired. Please try linking again.';
          break;
        case 'server_error':
          errorMessage = 'Server configuration error. Please contact support.';
          break;
        case 'invalid_token':
          errorMessage = 'Invalid session. Please log in again.';
          break;
        case 'discord_already_linked':
          errorMessage = 'This Discord account is already linked to another user.';
          break;
        case 'link_failed':
          errorMessage = 'Failed to link Discord account. Please try again.';
          break;
        case 'callback_error':
          errorMessage = 'OAuth callback error. Please try again.';
          break;
        default:
          errorMessage = 'An unknown error occurred.';
      }
      setMessage({ type: 'error', text: errorMessage });
    }

    if (success === 'discord_linked') {
      // Clear URL parameters first to prevent reload loop
      setSearchParams({});
      // Refresh user data by re-fetching profile
      setTimeout(() => {
        refreshUserData();
      }, 500); // Small delay to ensure the backend has processed the update
    }

    // Clear URL parameters for errors
    if (error) {
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleDiscordOAuth = async () => {
    setIsLoading(true);
    setMessage(null);

    // This will redirect to Discord OAuth - no need for try/catch as it redirects immediately
    await authAPI.initiateDiscordOAuth();
    // Note: Code after this won't execute because of the redirect
  };

  const handleUnlinkDiscord = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      await authAPI.updateDiscordId(null);

      // Update local user state
      if (user) {
        updateUser({ ...user, discord_user_id: undefined });
      }

      setMessage({
        type: 'success',
        text: 'Discord account unlinked successfully!'
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || error.response?.data?.error || 'Failed to unlink Discord account'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // TOTP/MFA Handlers
  const handleStartTOTPSetup = async () => {
    setTotpLoading(true);
    setMessage(null);

    try {
      const response = await authAPI.registerTOTP();
      setTotpUri(response.uri);
      setTotpSecret(response.secret);
      setTotpSetupMode(true);
    } catch (error: any) {
      const errorData = error.response?.data;
      if (errorData?.alreadyEnabled) {
        setMessage({
          type: 'error',
          text: 'MFA is already enabled on your account'
        });
        // Refresh status
        await loadTOTPStatus();
      } else {
        setMessage({
          type: 'error',
          text: errorData?.error || 'Failed to start MFA setup'
        });
      }
    } finally {
      setTotpLoading(false);
    }
  };

  const handleVerifyTOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setTotpLoading(true);
    setMessage(null);

    try {
      await authAPI.verifyTOTPSetup(totpVerifyCode);
      setMessage({
        type: 'success',
        text: 'Multi-Factor Authentication enabled successfully!'
      });
      setTotpSetupMode(false);
      setTotpUri('');
      setTotpSecret('');
      setTotpVerifyCode('');

      // Refresh TOTP status
      await loadTOTPStatus();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Invalid verification code'
      });
    } finally {
      setTotpLoading(false);
    }
  };

  const handleDisableTOTP = async () => {
    if (!window.confirm('Are you sure you want to disable Multi-Factor Authentication? This will make your account less secure.')) {
      return;
    }

    setTotpLoading(true);
    setMessage(null);

    try {
      await authAPI.removeTOTP();
      setMessage({
        type: 'success',
        text: 'Multi-Factor Authentication disabled successfully'
      });

      // Refresh TOTP status
      await loadTOTPStatus();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to disable MFA'
      });
    } finally {
      setTotpLoading(false);
    }
  };

  const handleCancelTOTPSetup = () => {
    setTotpSetupMode(false);
    setTotpUri('');
    setTotpSecret('');
    setTotpVerifyCode('');
    setMessage(null);
  };

  if (!user) {
    return (
      <div className="profile-container">
        <div className="error-message">Please log in to view your profile.</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <header className="page-header">
        <div className="header-content">
          <button
            className="back-button"
            onClick={() => navigate('/')}
          >
            ← Back to Dashboard
          </button>
          <div className="header-text">
            <h1>Profile Settings</h1>
            <p>Manage your account settings and integrations</p>
          </div>
        </div>
      </header>

      <div className="profile-content">
        <div className="profile-section">
          <h2>Account Information</h2>
          <div className="profile-info">
            <div className="info-item">
              <label>Display Name:</label>
              <span>{(user as any).displayName || user.username}</span>
            </div>
            <div className="info-item">
              <label>Username:</label>
              <span>{user.username}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{user.email || (user as any).zitadelProfile?.email || 'Not provided'}</span>
            </div>
            <div className="info-item">
              <label>Role:</label>
              <span className={`role-badge ${user.role}`}>{user.role}</span>
            </div>
          </div>
        </div>

        <div className="profile-section">
          <h2>Multi-Factor Authentication (MFA)</h2>
          <p className="section-description">
            Add an extra layer of security to your account with time-based one-time passwords (TOTP).
          </p>

          {totpStatusLoading ? (
            <p>Loading MFA status...</p>
          ) : totpSetupMode ? (
            <div className="mfa-setup-active">
              <h3>Scan QR Code</h3>
              <p>Scan this QR code with your authenticator app:</p>

              <div className="qr-code-container">
                {totpUri && (
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpUri)}`}
                    alt="TOTP QR Code"
                  />
                )}
              </div>

              <div className="manual-entry">
                <p><strong>Or enter this code manually:</strong></p>
                <code>{totpSecret}</code>
              </div>

              <form onSubmit={handleVerifyTOTP} className="mfa-verify-form">
                <div className="form-group">
                  <label htmlFor="totpCode">Enter the 6-digit code from your app:</label>
                  <input
                    type="text"
                    id="totpCode"
                    value={totpVerifyCode}
                    onChange={(e) => setTotpVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    required
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    disabled={totpLoading || totpVerifyCode.length !== 6}
                    className="btn-primary"
                  >
                    {totpLoading ? 'Verifying...' : 'Verify & Enable'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelTOTPSetup}
                    disabled={totpLoading}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          ) : totpEnabled ? (
            <div className="mfa-enabled">
              <div className="status-indicator connected"></div>
              <p><strong>MFA is enabled</strong> - Your account is protected with two-factor authentication.</p>
              <button
                type="button"
                onClick={handleDisableTOTP}
                disabled={totpLoading}
                className="btn-danger"
              >
                {totpLoading ? 'Disabling...' : 'Disable MFA'}
              </button>
            </div>
          ) : (
            <div className="mfa-setup">
              <p>Use an authenticator app like Google Authenticator, Authy, or 1Password to generate verification codes.</p>
              <button
                type="button"
                onClick={handleStartTOTPSetup}
                disabled={totpLoading}
                className="btn-primary"
              >
                {totpLoading ? 'Loading...' : '🔒 Enable MFA'}
              </button>
            </div>
          )}
        </div>

        <div className="profile-section">
          <h2>Discord Integration</h2>
          <p className="section-description">
            Link your Discord account to submit Pokemon and items directly from Discord using slash commands.
          </p>

          {user.discord_user_id ? (
            // Already linked - show status and unlink option
            <div className="discord-linked">
              <div className="discord-status">
                <div className="status-indicator connected"></div>
                <span>Discord account linked (ID: {user.discord_user_id})</span>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleUnlinkDiscord}
                  disabled={isLoading}
                  className="btn-danger"
                >
                  {isLoading ? 'Unlinking...' : 'Unlink Discord Account'}
                </button>
              </div>
            </div>
          ) : (
            // Not linked - show Discord OAuth only
            <div className="discord-linking">
              <div className="oauth-section">
                <h3>🔗 Link Discord Account</h3>
                <p>Connect your Discord account to submit Pokemon and items directly from Discord using slash commands. This uses Discord's secure OAuth system.</p>
                <button
                  type="button"
                  onClick={handleDiscordOAuth}
                  disabled={isLoading}
                  className="btn-discord"
                >
                  {isLoading ? 'Connecting...' : '🎮 Link with Discord'}
                </button>
              </div>
            </div>
          )}

          {message && (
            <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
              {message.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
